import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import type { AssetAuditProgress } from '@/types/audit';
import type { User } from '@/types/teams';

interface AssignWorkItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  asset: AssetAuditProgress;
  onSubmit: (data: WorkItemData) => void;
  users: User[];
  loadingUsers?: boolean;
}

interface WorkItemData {
  title: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  assignee: string;
  dueDate: Date | undefined;
  category: string;
  status: 'open' | 'in-progress' | 'blocked' | 'completed';
  blockedReason?: string;
}

export function AssignWorkItemDialog({
  open,
  onOpenChange,
  asset,
  onSubmit,
  users,
  loadingUsers = false,
}: AssignWorkItemDialogProps) {
  const [formData, setFormData] = useState<WorkItemData>({
    title: `Work Item for ${asset.assetCode}`,
    description: '',
    priority: 'medium',
    assignee: '',
    dueDate: undefined,
    category: 'maintenance',
    status: 'open',
    blockedReason: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate blocked reason if status is blocked
    if (formData.status === 'blocked' && !formData.blockedReason?.trim()) {
      return; // Form validation will handle this
    }
    
    onSubmit(formData);
    // Reset form
    setFormData({
      title: `Work Item for ${asset.assetCode}`,
      description: '',
      priority: 'medium',
      assignee: '',
      dueDate: undefined,
      category: 'maintenance',
      status: 'open',
      blockedReason: '',
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Assign Work Item</DialogTitle>
          <DialogDescription>
            Create a work item for asset {asset.assetCode} - {asset.assetName}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Enter work item title"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe the work that needs to be done..."
              rows={4}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="status">Status *</Label>
            <Select
              value={formData.status}
              onValueChange={(value: any) => {
                setFormData({ ...formData, status: value, blockedReason: value === 'blocked' ? formData.blockedReason : '' });
              }}
            >
              <SelectTrigger id="status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in-progress">In Progress</SelectItem>
                <SelectItem value="blocked">Blocked</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formData.status === 'blocked' && (
            <div className="space-y-2 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <Label htmlFor="blockedReason" className="text-amber-900 dark:text-amber-100">
                Reason for Blocking *
              </Label>
              <Textarea
                id="blockedReason"
                value={formData.blockedReason ?? ''}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData({ ...formData, blockedReason: e.target.value })}
                placeholder="Please explain why this work item is blocked..."
                rows={3}
                required
                className="bg-white dark:bg-gray-950"
            />
              <p className="text-xs text-amber-700 dark:text-amber-300">
                This information will help the team understand and resolve the blocker.
              </p>
          </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="category">Category *</Label>
              <Select
                value={formData.category}
                onValueChange={(value: string) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger id="category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                  <SelectItem value="repair">Repair</SelectItem>
                  <SelectItem value="inspection">Inspection</SelectItem>
                  <SelectItem value="replacement">Replacement</SelectItem>
                  <SelectItem value="upgrade">Upgrade</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="priority">Priority *</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(value: 'low' | 'medium' | 'high' | 'urgent') => setFormData({ ...formData, priority: value })}
                >
                <SelectTrigger id="priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="assignee">Assignee *</Label>
              {loadingUsers ? (
                <div className="flex items-center gap-2 h-10 px-3 border rounded-md text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading users...
                </div>
              ) : (
                <Select
                  value={formData.assignee}
                  onValueChange={(value: string) => setFormData({ ...formData, assignee: value })}
                >
                  <SelectTrigger id="assignee">
                    <SelectValue placeholder="Select assignee" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.filter(u => u.status === 'active').map(user => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name}
                      </SelectItem>
                    ))}
                    {users.filter(u => u.status === 'active').length === 0 && (
                      <SelectItem value="__no_users" disabled>
                        No users available
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="space-y-2">
              <Label>Due Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !formData.dueDate && 'text-muted-foreground'
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.dueDate ? format(formData.dueDate, 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.dueDate}
                    onSelect={(date) => setFormData({ ...formData, dueDate: date })}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="bg-muted/50 p-4 rounded-lg space-y-2">
            <h4 className="font-medium text-sm">Asset Information</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-muted-foreground">Asset Code:</span>
                <span className="ml-2 font-medium">{asset.assetCode}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Location:</span>
                <span className="ml-2 font-medium">{asset.currentLocation}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Category:</span>
                <span className="ml-2 font-medium">{asset.category}</span>
              </div>
              {asset.condition && (
                <div>
                  <span className="text-muted-foreground">Condition:</span>
                  <span className="ml-2 font-medium capitalize">{asset.condition}</span>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit">Create Work Item</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
